import React, { useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../firebase/useAuth';
import { backendURL, PAYPAL_MONTHLY_PLAN_ID, PAYPAL_YEARLY_PLAN_ID } from '../../App';

function PayPalButton({ onSuccess, onError, selectedPlan }) {
  const paypalRef = useRef(null);
  const { userId } = useAuth();
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  useEffect(() => {
    const paypalContainer = paypalRef.current;
    // Ensure all dependencies are available
    if (!paypalContainer || !userId || !selectedPlan || !window.paypal) {
      return;
    }

    // This check is still useful to prevent unnecessary re-renders
    // if the parent component updates for other reasons.
    if (paypalContainer.hasChildNodes()) {
      return;
    }

    // Define the button options based on the plan
    const getButtonOptions = () => {
      if (selectedPlan === 'Lifetime') {
        return {
          style: { shape: 'pill', color: 'black', layout: 'vertical', label: 'pay' },
          createOrder: (data, actions) =>
            actions.order.create({
              purchase_units: [
                {
                  amount: { value: '149.99' },
                  custom_id: userId,
                },
              ],
              application_context: { shipping_preference: 'NO_SHIPPING' },
            }),
          onApprove: async (data, actions) => {
            await actions.order.capture();
            // Attempt immediate activation — if this fails, PAYMENT.CAPTURE.COMPLETED webhook is the fallback
            try {
              await axios.post(`${backendURL}/activateSubscription`, {
                type: 'order',
                id: data.orderID,
                userId,
              });
            } catch (err) {
              console.error('Immediate activation failed, webhook will activate subscription:', err);
            }
            onSuccessRef.current('Lifetime');
          },
          onError: (err) => onErrorRef.current(err),
        };
      } else {
        const planId = selectedPlan === 'Monthly' ? PAYPAL_MONTHLY_PLAN_ID : PAYPAL_YEARLY_PLAN_ID;
        return {
          style: { shape: 'pill', color: 'black', layout: 'vertical', label: 'subscribe' },
          createSubscription: (data, actions) =>
            actions.subscription.create({
              plan_id: planId,
              custom_id: userId,
            }),
          onApprove: async (data) => {
            // Attempt immediate activation — if this fails, PAYMENT.SALE.COMPLETED webhook is the fallback
            try {
              await axios.post(`${backendURL}/activateSubscription`, {
                type: 'subscription',
                id: data.subscriptionID,
                userId,
              });
            } catch (err) {
              console.error('Immediate activation failed, webhook will activate subscription:', err);
            }
            onSuccessRef.current(selectedPlan);
          },
          onError: (err) => onErrorRef.current(err),
        };
      }
    };

    // Create the button instance
    const buttons = window.paypal.Buttons(getButtonOptions());

    // Render the button and handle potential errors
    buttons.render(paypalContainer).catch((err) => {
      console.error('Failed to render PayPal Buttons', err);
      onErrorRef.current('Failed to render PayPal Buttons.');
    });

    // **IMPORTANT:** Use the official .close() method for cleanup.
    // This is safe for React's Strict Mode.
    return () => {
      buttons.close();
    };
  }, [userId, selectedPlan]); // Dependency array is stable

  return <div ref={paypalRef}></div>;
}

export default PayPalButton;
